import { Check } from 'lucide-react';
import { Button } from '../ui/button';
import { PK_COLORS } from '../PathkitLogo';

interface Tier {
  name: string;
  price: string;
  cadence?: string;
  blurb: string;
  bullets: string[];
  cta: { label: string; href: string };
  highlighted?: boolean;
}

const tiers: Tier[] = [
  {
    name: 'Free',
    price: '$0',
    cadence: 'forever',
    blurb: 'Try PathKit on a single engagement.',
    bullets: [
      '1 user',
      '1 active client company',
      'Community support',
      'Atlassian template only',
    ],
    cta: { label: 'Get started', href: '/login' },
  },
  {
    name: 'Solo',
    price: '$299',
    cadence: 'per month',
    blurb: 'For independent consultants running their own book.',
    bullets: [
      '1 user (hard limit)',
      'Up to 5 active client companies',
      'Atlassian and Custom templates',
      'Branded client portal',
      'Email support',
    ],
    cta: { label: 'Book a demo', href: '/book-demo' },
  },
  {
    name: 'Starter',
    price: '$499',
    cadence: 'per month',
    blurb: 'Small teams running multiple service desk projects.',
    bullets: [
      '10 users included ($25 / extra user)',
      'Up to 50 active companies (scales with users)',
      'All template types (Atlassian, ServiceNow, Ivanti, Generic, Custom)',
      'Custom branding + API access',
      'Priority email support',
    ],
    cta: { label: 'Book a demo', href: '/book-demo' },
  },
  {
    name: 'Premium',
    price: '$1,299',
    cadence: 'per month',
    blurb: 'Growing firms running enterprise service desk implementations.',
    bullets: [
      '25 users included ($35 / extra user)',
      'Up to 125 active companies (scales with users)',
      'All template types',
      'Advanced branding + API access',
      'Priority phone & email support',
      'Dedicated account manager',
    ],
    cta: { label: 'Book a demo', href: '/book-demo' },
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: '$4,999',
    cadence: 'per month',
    blurb: 'Large firms managing complex portfolios at scale.',
    bullets: [
      '100 users included ($45 / extra user)',
      'Up to 500 active companies (scales with users)',
      'White-label + custom domain',
      '24/7 premium support, SLAs',
      'Custom integrations',
      'Advanced security + on-premise options',
    ],
    cta: { label: 'Talk to sales', href: '/book-demo' },
  },
];

export function MarketingPricing() {
  return (
    <section id="pricing" className="py-20 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight"
            style={{ color: PK_COLORS.navy }}
          >
            Simple pricing that scales with your firm
          </h2>
          <p className="mt-4 text-slate-600">
            Pay monthly, no contracts. Add users any time &mdash; your client-company limit grows with you.
          </p>
        </div>

        <ul className="mt-12 grid gap-4 lg:grid-cols-5 md:grid-cols-2">
          {tiers.map((t) => (
            <li
              key={t.name}
              className={
                'rounded-xl border bg-white p-6 flex flex-col ' +
                (t.highlighted
                  ? 'border-2 shadow-md'
                  : 'border-slate-200')
              }
              style={t.highlighted ? { borderColor: PK_COLORS.teal } : undefined}
            >
              {t.highlighted && (
                <div
                  className="self-start text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full mb-2"
                  style={{ backgroundColor: PK_COLORS.teal, color: '#ffffff' }}
                >
                  Most popular
                </div>
              )}
              <h3 className="text-lg font-semibold" style={{ color: PK_COLORS.navy }}>
                {t.name}
              </h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-3xl font-semibold" style={{ color: PK_COLORS.navy }}>
                  {t.price}
                </span>
                {t.cadence && (
                  <span className="text-sm text-slate-500">/ {t.cadence}</span>
                )}
              </div>
              <p className="mt-2 text-sm text-slate-600">{t.blurb}</p>

              <ul className="mt-5 space-y-2 flex-1">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: PK_COLORS.teal }}
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <Button
                asChild
                className={t.highlighted ? 'mt-6 hover:opacity-90' : 'mt-6'}
                variant={t.highlighted ? 'default' : 'outline'}
                style={
                  t.highlighted
                    ? { backgroundColor: PK_COLORS.navy, color: '#ffffff' }
                    : undefined
                }
              >
                <a
                  href={t.cta.href}
                  style={t.highlighted ? { color: '#ffffff' } : undefined}
                >
                  {t.cta.label}
                </a>
              </Button>
            </li>
          ))}
        </ul>

        <p className="mt-8 text-xs text-slate-500">
          Prices in USD. Annual pricing available on request &mdash;{' '}
          <a href="/book-demo" className="underline hover:text-slate-700">
            talk to sales
          </a>
          .
        </p>
      </div>
    </section>
  );
}
